import { Injectable, signal } from '@angular/core';

export interface FormattedDateRange {
    startDate: string;
    endDate: string;
    fullRange: string;
}

type FormatGroup = 'group1' | 'group2' | 'group3';

@Injectable({
    providedIn: 'root',
})
export class DateFormattingService {
    // Locale to Format Group mapping
    private readonly LOCALE_TO_GROUP: Map<string, FormatGroup> = new Map([
        // Group 1: Day Month - Month Year (European/Latin)
        ['de-DE', 'group1'],
        ['en-GB', 'group1'],
        ['fr-FR', 'group1'],
        ['es-ES', 'group1'],
        ['it-IT', 'group1'],
        ['pt-PT', 'group1'],
        ['pl-PL', 'group1'],

        // Group 2: Month Day - Month Day (US)
        ['en-US', 'group2'],

        // Group 3: Year Month/Day Format (Asian)
        ['ja-JP', 'group3'],
        ['zh-CN', 'group3'],
        ['ko-KR', 'group3'],
    ]);
        public readonly localeSignal = signal<string>(localStorage.getItem('language') ?? 'de-DE');
    constructor() {
        // Listen for storage events (when language is changed in other tabs)
        window.addEventListener('storage', (event) => {
            if (event.key === 'language' && event.newValue) {
                this.localeSignal.set(event.newValue);
            }
        });
    }

    /**
     * Get current locale from localStorage - called every time to ensure updates
     */
    public getCurrentLocale(): string {
        return localStorage.getItem('language') ?? 'de-DE';
    }

    /**
     * Get format group for current locale
     */
    private getFormatGroupInternal(): FormatGroup {
        const locale = this.getCurrentLocale();
        if (this.LOCALE_TO_GROUP.has(locale)) {
            return this.LOCALE_TO_GROUP.get(locale)!;
        }
        return 'group1';
    }

        /**
     * Formats a date range based on duration and locale group
     */
    formatDateRange(startDate: string | Date, endDate: string | Date, locale?: string): FormattedDateRange {
       this.localeSignal();

        const start = this.parseDate(startDate);
        const end = this.parseDate(endDate);
        if (!start || !end) {
            return this.getDefaultResponse();
        }

        // Calculate duration in months (calendar-based, not days)
        let monthDifference = (end.getFullYear() - start.getFullYear()) * 12 +
                                (end.getMonth() - start.getMonth());
        // If end day is before start day, we haven't completed a full month
        if (end.getDate() < start.getDate()) {
            monthDifference--;
        }
        const formatGroup = this.getFormatGroupInternal();

        // Determine formatting based on actual month duration
        if (monthDifference > 6) {
            // > 6 months
            return this.formatLongDuration(start, end);
        } else if (monthDifference > 0) {
            // 1-6 months (medium duration)
            return this.formatMediumDuration(start, end, formatGroup);
        } else {
            // Same month or less than 1 month (short duration)
            return this.formatShortDuration(start, end, formatGroup);
        }
    }

    /**
     * Checks if two dates represent the same day
     */
    isOneDayEvent(startDate: string | Date, endDate: string | Date): boolean {
        const start = this.parseDate(startDate);
        const end = this.parseDate(endDate);

        if (!start || !end) {
            return false;
        }

        return (
            start.getDate() === end.getDate() &&
            start.getMonth() === end.getMonth() &&
            start.getFullYear() === end.getFullYear()
        );
    }

    /**
     * Formats long duration (> 6 months)
     * Same for all groups: "2025" or "2025–2026"
     */
    private formatLongDuration(start: Date, end: Date): FormattedDateRange {
    const startYear = start.getFullYear();
    const endYear = end.getFullYear();

    if (startYear === endYear) {
        // Condition 1: Same year, show only one year
        const range = `${endYear}`;
        return {
            startDate: '',
            endDate: startYear.toString(),
            fullRange: range,
        };
    } else {
        // Condition 2: Spans multiple years, show both
        const range = `${startYear}–${endYear}`;
        return {
            startDate: startYear.toString(),
            endDate: endYear.toString(),
            fullRange: range,
        };
    }
}

    /**
     * Formats medium duration (> 1 month, <= 6 months)
     * Uses Intl.DateTimeFormat for localized month names
     */
    private formatMediumDuration(
        start: Date,
        end: Date,
        group: FormatGroup,
    ): FormattedDateRange {
        const locale = this.getCurrentLocale();
        const startMonth = this.getLocalizedMonth(start, locale, group);
        const endMonth = this.getLocalizedMonth(end, locale, group);
        const startYear = start.getFullYear();
        const endYear = end.getFullYear();

        // Check if spans multiple years
        const spansMultipleYears = startYear !== endYear;

        if (group === 'group1') {
            // de-DE, en-GB, fr-FR
            if (spansMultipleYears) {
                // Multiple years: "Sep 2026–Jan 2027"
                const fullRange = `${startMonth} ${startYear}–${endMonth} ${endYear}`;
                return {
                    startDate: `${startMonth} ${startYear}`,
                    endDate: `${endMonth} ${endYear}`,
                    fullRange: fullRange,
                };
            } else {
                // Same year: "Jun–Jul 2025"
                const fullRange = `${startMonth}–${endMonth} ${endYear}`;
                return {
                    startDate: `${startMonth}`,
                    endDate: `${endMonth} ${endYear}`,
                    fullRange: fullRange,
                };
            }
        } else if (group === 'group2') {
            // en-US
            if (spansMultipleYears) {
                // Multiple years: "Sep 2026–Jan 2027"
                const fullRange = `${startMonth} ${startYear}–${endMonth} ${endYear}`;
                return {
                    startDate: `${startMonth} ${startYear}`,
                    endDate: `${endMonth} ${endYear}`,
                    fullRange: fullRange,
                };
            } else {
                // Same year: "Jun–Jul 2025"
                const fullRange = `${startMonth}–${endMonth} ${endYear}`;
                return {
                    startDate: `${startMonth}`,
                    endDate: `${endMonth} ${endYear}`,
                    fullRange: fullRange,
                };
            }
        } else {
            // ja-JP, zh-CN, ko-KR
            if (spansMultipleYears) {
                // Multiple years: "2026年9月–2027年1月"
                const fullRange = `${startYear}${this.getYearSuffix(locale)}${startMonth}–${endYear}${this.getYearSuffix(locale)}${endMonth}`;
                return {
                    startDate: `${startMonth}`,
                    endDate: `${endMonth}`,
                    fullRange: fullRange,
                };
            } else {
                // Same year: "2025年6月–7月"
                const fullRange = `${endYear}${this.getYearSuffix(locale)}${startMonth}–${endMonth}`;
                return {
                    startDate: `${startMonth}`,
                    endDate: `${endMonth}`,
                    fullRange: fullRange,
                };
            }
        }
    }

    /**
     * Formats short duration (<= 1 month)
     * Uses Intl.DateTimeFormat for localized month names
     */
    private formatShortDuration(
        start: Date,
        end: Date,
        group: FormatGroup,
    ): FormattedDateRange {
        const locale = this.getCurrentLocale();
        const startDay = start.getDate();
        const endDay = end.getDate();
        const startMonth = this.getLocalizedMonth(start, locale, group);
        const endMonth = this.getLocalizedMonth(end, locale, group);
        const year = end.getFullYear();
        const startYear = start.getFullYear();
        const endYear = end.getFullYear();

        // Check if both dates are in the same month
        const isSameMonth = start.getMonth() === end.getMonth();
        const spansMultipleYears = startYear !== endYear;

        if (group === 'group1') {
            // de-DE, en-GB, fr-FR
            // de-DE, en-GB, fr-FR
            if (spansMultipleYears) {
                // Multiple years: "30 Dec 2026–2 Jan 2027"
                const fullRange = `${startDay} ${startMonth} ${startYear}–${endDay} ${endMonth} ${endYear}`;
                return {
                    startDate: `${startDay} ${startMonth} ${startYear}`,
                    endDate: `${endDay} ${endMonth} ${endYear}`,
                    fullRange: fullRange,
                };
            }
           else if (isSameMonth) {
                if(startDay==endDay){
                    const fullRange = `${startDay} ${startMonth} ${year}`;
                    return {
                        startDate: `${startDay}`,
                        endDate: `${startDay} ${startMonth} ${year}`,
                        fullRange: fullRange,
                    };
                }
                else{
                    const fullRange = `${startDay}–${endDay} ${startMonth} ${year}`;
                return {
                    startDate: `${startDay}`,
                    endDate: `${endDay} ${startMonth} ${year}`,
                    fullRange: fullRange,
                };
                }
            } else {
                // Different months: "30 Jun - 7 Jul 2025"
                const fullRange = `${startDay} ${startMonth} - ${endDay} ${endMonth} ${year}`;
                return {
                    startDate: `${startDay} ${startMonth}`,
                    endDate: `${endDay} ${endMonth} ${year}`,
                    fullRange: fullRange,
                };
            }
        } else if (group === 'group2') {
            // en-US
            if (spansMultipleYears) {
                // Multiple years: "Dec 30, 2026–Jan 2, 2027"
                const fullRange = `${startMonth} ${startDay}, ${startYear}–${endMonth} ${endDay}, ${endYear}`;
                return {
                    startDate: `${startMonth} ${startDay}, ${startYear}`,
                    endDate: `${endMonth} ${endDay}, ${endYear}`,
                    fullRange: fullRange,
                };
            }
            else if (isSameMonth) {
                // Same month: "Jun 3–7, 2025"
                if(startDay==endDay){
                    const fullRange = `${startDay} ${startMonth} ${year}`;
                    return {
                        startDate: `${startDay}`,
                        endDate: `${startDay} ${startMonth} ${year}`,
                        fullRange: fullRange,
                    };
                }
                else {
                    const fullRange = `${startMonth} ${startDay}–${endDay}, ${year}`;
                    return {
                        startDate: `${startMonth} ${startDay}`,
                        endDate: `${endDay}, ${year}`,
                        fullRange: fullRange,
                    };
                }
            } else {
                // Different months: "Jun 30 - Jul 7, 2025"
                const fullRange = `${startMonth} ${startDay} - ${endMonth} ${endDay}, ${year}`;
                return {
                    startDate: `${startMonth} ${startDay}`,
                    endDate: `${endMonth} ${endDay}, ${year}`,
                    fullRange: fullRange,
                };
            }
        } else {
            // ja-JP, zh-CN, ko-KR: "2025年6月30日–7月7日"
            if (spansMultipleYears) {
                // Multiple years: "2026年12月30日–2027年1月2日"
                const fullRange = `${startYear}${this.getYearSuffix(locale)}${startMonth}${startDay}${this.getDaySuffix(locale)}–${endYear}${this.getYearSuffix(locale)}${endMonth}${endDay}${this.getDaySuffix(locale)}`;
                return {
                    startDate: `${startMonth}${startDay}${this.getDaySuffix(locale)}`,
                    endDate: `${endMonth}${endDay}${this.getDaySuffix(locale)}`,
                    fullRange: fullRange,
                };
            } else {
                // Same year: "2025年6月30日–7月7日"
                const fullRange = `${startYear}${this.getYearSuffix(locale)}${startMonth}${startDay}${this.getDaySuffix(locale)}–${endMonth}${endDay}${this.getDaySuffix(locale)}`;
                return {
                    startDate: `${startMonth}${startDay}${this.getDaySuffix(locale)}`,
                    endDate: `${endMonth}${endDay}${this.getDaySuffix(locale)}`,
                    fullRange: fullRange,
                };
            }
        }
    }

    /**
     * Gets localized month name using Intl.DateTimeFormat API
     * - Asian locales (ko-KR, ja-JP, zh-CN): uses 'long' format → "6월", "6月"
     * - European/US locales: uses 'short' format → "Jun", "Jul"
     */
    private getLocalizedMonth(date: Date, locale: string, group: FormatGroup): string {
        const monthFormat = group === 'group3' ? 'long' : 'short';
        const formatter = new Intl.DateTimeFormat(locale, { month: monthFormat });
        const monthName = formatter.format(date);
        // Restrict to 3 characters
        return monthName.substring(0, 3);
    }

    /**
     * Gets locale-specific year suffix
     */
    private getYearSuffix(locale: string): string {
        if (locale.startsWith('ja')) return '年';
        if (locale.startsWith('zh')) return '年';
        if (locale.startsWith('ko')) return '년 ';  // Add space after Korean year marker
        return ' ';
    }

    /**
     * Gets locale-specific day suffix
     */
    private getDaySuffix(locale: string): string {
        if (locale.startsWith('ja')) return '日';
        if (locale.startsWith('zh')) return '日';
        if (locale.startsWith('ko')) return '일';
        return '';
    }

    /**
     * Set locale
     */
    setLocale(locale: string): void {
        localStorage.setItem('language', locale);
    }

    getLocale(): string {
        return this.getCurrentLocale();
    }

    getFormatGroup(): FormatGroup {
        return this.getFormatGroupInternal();
    }

    private parseDate(date: string | Date): Date | null {
        try {
            if (date instanceof Date) {
                return new Date(date);
            }
            const parsed = new Date(date);
            return !isNaN(parsed.getTime()) ? parsed : null;
        } catch {
            return null;
        }
    }

    private getDefaultResponse(): FormattedDateRange {
        return {
            startDate: '',
            endDate: '',
            fullRange: '',
        };
    }

    public notifyLocaleChanged(locale: string): void {
        this.localeSignal.set(locale);
    }
}
